// Live Dexie queries. Every view renders from these; there are no loading
// states for data (only `undefined` on the very first render).
import { paletteFor, personLayerOf, studioOf, type Look, type PersonLook, type StudioLook } from '@/lib/look'
import {
  appearanceOf,
  layerOfCarrier,
  resolveLayers,
  type Layers,
  type ResolvedLook,
} from '@/lib/themeLayers'
import { resolveAppearance, type Resolved } from '@/lib/horizon'
import { useNow } from '@/hooks/useNow'
import { useDevice } from '@/state/appearance'
import { pageThreadOf } from '@/lib/panel'
import { useMemo } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '@/runtime'
import { META } from './schema'
import type { Edge, OpRecord, Resource } from './types'
import type { TaskResource } from '@/kinds/task/apply'
import type { ProjectResource } from '@/kinds/project/apply'
import { settingsDefaults, type SettingsSpec } from '@/kinds/settings'
import { settingsIdNow, useAccess } from '@/state/access'
import type { PageResource } from '@/kinds/page/apply'
import type { ListResource } from '@/kinds/list/apply'
import type { GrantResource } from '@/kinds/grant/apply'
import type { FileResource } from '@/kinds/file/apply'
import type { DateResource } from '@/kinds/date/apply'
import type { PersonResource } from '@/kinds/person/apply'
import type { DayResource } from '@/kinds/day/apply'
import type { WorkspaceResource } from '@/kinds/workspace/apply'
import type { ThemeProfileResource } from '@/kinds/themeprofile/apply'
import type {
  ConceptResource,
  CourseResource,
  ExerciseResource,
  LessonResource,
  ModuleResource,
  SessionResource,
} from '@/kinds/learn/apply'
import type { SuggestionResource } from '@/kinds/suggestion'
import type { AgentSessionResource } from '@/kinds/agentsession'
import type { AgentRunResource } from '@/kinds/agentrun/apply'
import type { PullRequestResource } from '@/kinds/git/apply'
import { ALL } from '@/kinds/workspace/schema'
import { useScope } from '@/state/scope'

/**
 * The last result of each live query, by its inputs. `useLiveQuery` returns
 * nothing on a component's first render, so every page that mounted drew once
 * empty or with defaults and then jumped to the real thing (Home's layout,
 * Apps' columns, a list popping in). Starting from the last result instead
 * means a page that comes back paints what it showed before, and the query
 * brings anything newer a moment later. The first render of the whole app is
 * the only one that can still be empty (settings are kept on the device for
 * that, below).
 */
const lastSeen = new Map<string, unknown>()
const useLive = <T>(key: string, query: () => Promise<T>, deps: unknown[]): T | undefined =>
  useLiveQuery(
    async () => {
      const r = await query()
      lastSeen.set(key, r)
      return r
    },
    deps,
    lastSeen.get(key) as T | undefined,
  )

export const useMeta = <T>(key: string): T | undefined => useLiveQuery(() => db.getMeta<T>(key), [key])

export const useResource = <S = Record<string, unknown>, T = Record<string, unknown>>(
  id: string | undefined,
) =>
  useLive(
    `Res:${id ?? ''}`,
    async () => (id ? ((await db.resources.get(id)) as Resource<S, T> | undefined) : undefined),
    [id],
  )

/**
 * One record for a page about it: undefined while the first read is on its way,
 * null once it has answered that there is no such record, so the page can tell
 * "not read yet" (draw nothing) from "gone" (say so) and never flash either.
 * Like every live hook it starts from the last answer for this id, so a thread
 * or page you come back to paints whole on its first frame.
 */
export const useRecord = <S = Record<string, unknown>, T = Record<string, unknown>>(id: string | undefined) =>
  useLive(
    `Rec:${id ?? ''}`,
    async () => (id ? (((await db.resources.get(id)) as Resource<S, T> | undefined) ?? null) : null),
    [id],
  )

const live = <R extends Resource>(rows: R[]) => rows.filter((r) => !r.deleted_at)

/** Live rows of a kind in a workspace scope (`*` = all). */
const byKind = async <R extends Resource>(kind: string, ws: string): Promise<R[]> =>
  live(
    (ws === ALL
      ? await db.resources.where('kind').equals(kind).toArray()
      : await db.resources.where('[labels.ws+kind]').equals([ws, kind]).toArray()) as R[],
  )

/** All live tasks in the current workspace (templates included; callers filter). */
export const useTasks = (): TaskResource[] | undefined => {
  const ws = useScope((s) => s.ws)
  return useLive(`Task:${ws}`, () => byKind<TaskResource>('Task', ws), [ws])
}

/** Every live task regardless of workspace (search, cross-workspace lookups). */
export const useAllTasks = (): TaskResource[] | undefined =>
  useLive(`Task:${ALL}`, () => byKind<TaskResource>('Task', ALL), [])

export const useTasksByState = (state: string): TaskResource[] | undefined => {
  const ws = useScope((s) => s.ws)
  return useLive(
    `TaskState:${state}:${ws}`,
    async () => (await byKind<TaskResource>('Task', ws)).filter((t) => t.status.state === state),
    [state, ws],
  )
}

export const useProjects = (): ProjectResource[] | undefined => {
  const ws = useScope((s) => s.ws)
  return useLive(`Project:${ws}`, () => byKind<ProjectResource>('Project', ws), [ws])
}

export const useAllProjects = (): ProjectResource[] | undefined =>
  useLive(`Project:${ALL}`, () => byKind<ProjectResource>('Project', ALL), [])

export const useWorkspaces = (): WorkspaceResource[] =>
  useLive(`Workspace:${ALL}`, () => byKind<WorkspaceResource>('Workspace', ALL), []) ?? []

/** True once the suites have been read from this device (a cold start has not, for a frame). */
export const useSuitesRead = (): boolean =>
  useLive(`Workspace:${ALL}`, () => byKind<WorkspaceResource>('Workspace', ALL), []) !== undefined

/** slug → workspace, for icons on rows in Everything. */
export const useWorkspaceBySlug = (): Map<string, WorkspaceResource> => {
  const ws = useWorkspaces()
  return new Map(ws.map((w) => [w.spec.slug, w]))
}

/** Connector mirrors of a kind in the current scope. */
export const useMirrors = (kind: string): Resource[] | undefined => {
  const ws = useScope((s) => s.ws)
  return useLive(`Mirror:${kind}:${ws}`, () => byKind<Resource>(kind, ws), [kind, ws])
}

/** Connector mirrors of a kind in every workspace (the graph sees the whole factory). */
export const useAllMirrors = (kind: string): Resource[] | undefined =>
  useLive(`Mirrors:${kind}:${ALL}`, () => byKind<Resource>(kind, ALL), [kind])

const newestRun = (a: AgentRunResource, b: AgentRunResource) =>
  a.spec.updated_at < b.spec.updated_at ? 1 : -1

/**
 * Factory runs handed off from one task, newest first. Every workspace: a run
 * lives in the factory connector's workspace, which need not be the task's.
 */
export const useRunsForTask = (taskId: string): AgentRunResource[] =>
  useLive(
    `AgentRun:task:${taskId}`,
    async () =>
      (await byKind<AgentRunResource>('AgentRun', ALL))
        .filter((r) => r.spec.source.provider === 'factory' && r.spec.source_ref === taskId)
        .sort(newestRun),
    [taskId],
  ) ?? []

/**
 * The coding agents of the current workspace, newest first: a session belongs
 * to the workspace it was started in (Everything shows them all).
 */
export const useRuns = (): AgentRunResource[] | undefined => {
  const ws = useScope((s) => s.ws)
  return useLive(
    `AgentRun:${ws}`,
    async () => (await byKind<AgentRunResource>('AgentRun', ws)).sort(newestRun),
    [ws],
  )
}

/** Every agent session whatever the scope, for lookups (repos an agent has used). */
export const useAllRuns = (): AgentRunResource[] | undefined =>
  useLive(
    `AgentRun:${ALL}`,
    async () => (await byKind<AgentRunResource>('AgentRun', ALL)).sort(newestRun),
    [],
  )

/**
 * What the Pipeline reads from the device (lib/flow.ts), every workspace, in one
 * query: the coding agents, the pull request mirrors and the pending merge
 * cards, so a tap on Merge moves its row the moment the card syncs, without
 * waiting for the next poll of /api/pipeline.
 */
export const useFlowRecords = ():
  { runs: AgentRunResource[]; prs: PullRequestResource[]; cards: SuggestionResource[] } | undefined =>
  useLive(
    'FlowRecords',
    async () => ({
      runs: await byKind<AgentRunResource>('AgentRun', ALL),
      prs: await byKind<PullRequestResource>('PullRequest', ALL),
      // pending merge cards, and the merged ones of a batch in the last 6 h (a batch counts them as in)
      cards: (await byKind<SuggestionResource>('Suggestion', ALL)).filter(
        (s) =>
          !s.deleted_at &&
          s.spec.ops.some((o) => o.type === 'merge_pr') &&
          (s.status.state === 'pending' ||
            (s.status.state === 'accepted' &&
              s.status.merge?.state === 'merged' &&
              !!s.status.merge.batch &&
              Date.parse(s.updated_at) > Date.now() - 6 * 3_600_000)),
      ),
    }),
    [],
  )

/** Pending suggestions in the current scope, newest first. */
export const useSuggestions = (): SuggestionResource[] => {
  const ws = useScope((s) => s.ws)
  return (
    useLive(
      `Suggestions:${ws}`,
      async () =>
        (await byKind<SuggestionResource>('Suggestion', ws))
          .filter((s) => s.status.state === 'pending' && !s.spec.session_id)
          .sort((a, b) => (a.created_at < b.created_at ? 1 : -1)),
      [ws],
    ) ?? []
  )
}

/**
 * The cards in the merge queue (queued, merging or failed), every workspace:
 * what a merge card counts its position from (lib/mergeQueue.ts).
 */
export const useMergeCards = (): SuggestionResource[] =>
  useLive(
    'MergeCards',
    async () =>
      (await byKind<SuggestionResource>('Suggestion', ALL)).filter(
        (s) => !s.deleted_at && s.status.state === 'pending' && !!s.status.merge,
      ),
    [],
  ) ?? []

/**
 * The cards a session proposed (Prime on Claude Code) that still wait for the
 * owner, in every workspace, oldest first. Decided cards leave the thread: what
 * they started shows as state in Prime's top bar, and its words say the rest.
 */
export const useSessionCards = (sessionId: string): SuggestionResource[] =>
  useLive(
    `SessionCards:${sessionId}`,
    async () => {
      return (await byKind<SuggestionResource>('Suggestion', ALL))
        .filter((s) => s.spec.session_id === sessionId && !s.deleted_at && s.status.state === 'pending')
        .sort((a, b) => (a.created_at < b.created_at ? -1 : 1))
        .slice(-8)
    },
    [sessionId],
  ) ?? []

/**
 * Every agent session and the lead's waiting cards, read together: what is
 * being waited on (lib/waiting.ts) needs both, and read apart the runs landed
 * a moment before the cards, so a page drew "no merge card yet" beside a merge
 * card that was there all along. The lead is the newest Prime session not over.
 */
export const useRunsAndLeadCards = ():
  { runs: AgentRunResource[]; cards: SuggestionResource[]; leadId?: string } | undefined =>
  useLive(
    'RunsAndLeadCards',
    async () => {
      const runs = (await byKind<AgentRunResource>('AgentRun', ALL)).sort(newestRun)
      const lead = runs
        .filter((r) => r.spec.template === 'prime' && r.spec.status !== 'Ended' && r.spec.status !== 'Failed')
        .sort((a, b) => (b.spec.started_at || '').localeCompare(a.spec.started_at || ''))[0]
      const cards = lead
        ? (await byKind<SuggestionResource>('Suggestion', ALL))
            .filter((s) => s.spec.session_id === lead.id && s.status.state === 'pending')
            .sort((a, b) => (a.created_at < b.created_at ? -1 : 1))
            .slice(-8)
        : []
      return { runs, cards, leadId: lead?.id }
    },
    [],
  )

/** Suggestions by id in any state (for chat turns). */
/** Capture receipts (the assistant filed a capture), newest first, every state. */
export const useCaptureReceipts = (): SuggestionResource[] => {
  const ws = useScope((s) => s.ws)
  return (
    useLive(
      `Captures:${ws}`,
      async () =>
        (await byKind<SuggestionResource>('Suggestion', ws))
          .filter((s) => s.spec.type === 'capture' && !s.deleted_at)
          .sort((a, b) => (a.created_at < b.created_at ? 1 : -1)),
      [ws],
    ) ?? []
  )
}

export const useSuggestionsByIds = (ids: string[]): SuggestionResource[] => {
  const key = ids.join(',')
  return (
    useLive(
      `SuggestionIds:${key}`,
      async () =>
        key ? ((await db.resources.bulkGet(key.split(','))).filter(Boolean) as SuggestionResource[]) : [],
      [key],
    ) ?? []
  )
}

export const useAgentSessions = (): AgentSessionResource[] => {
  const ws = useScope((s) => s.ws)
  return (
    useLive(
      `Sessions:${ws}`,
      async () =>
        (await byKind<AgentSessionResource>('AgentSession', ws)).sort((a, b) =>
          (a.status.last_at ?? a.created_at) < (b.status.last_at ?? b.created_at) ? 1 : -1,
        ),
      [ws],
    ) ?? []
  )
}

/** A page's own thread (the desktop panel and the phone's Agent tab), found whatever the scope. */
export const usePageThread = (pageId: string): AgentSessionResource | undefined =>
  useLive(
    `PageThread:${pageId}`,
    async () => pageThreadOf(await byKind<AgentSessionResource>('AgentSession', ALL), pageId) ?? null,
    [pageId],
  ) ?? undefined

export const useDecidedSuggestions = (): SuggestionResource[] => {
  const ws = useScope((s) => s.ws)
  return (
    useLive(
      `Decided:${ws}`,
      async () =>
        (await byKind<SuggestionResource>('Suggestion', ws)).filter((s) => s.status.state === 'accepted'),
      [ws],
    ) ?? []
  )
}

/** Every ConnectorConfig resource. */
export const useConnectorConfigs = (): Resource[] =>
  useLive(
    'ConnectorConfigs',
    async () => live((await db.resources.where('kind').equals('ConnectorConfig').toArray()) as Resource[]),
    [],
  ) ?? []

/** The current workspace resource, or undefined in Everything. */
export const useCurrentWorkspace = (): WorkspaceResource | undefined => {
  const scope = useScope((s) => s.ws)
  return useWorkspaces().find((w) => w.spec.slug === scope)
}

/** The three layers of the look (lib/themeLayers.ts): this device's, the person's (the owner's, an admin member's) and the workspace's. */
export const useLayers = (): Layers => {
  const current = useCurrentWorkspace()
  const settings = useSettings()
  const member = useAccess((s) => s.member)
  const admin = useAccess((s) => s.admin)
  const device = useDevice((s) => s.layer)
  const slug = current?.spec.slug ?? ALL
  // a plain member has no Settings record that syncs, so no layer of her own
  const own = !member || admin ? (settings.person_look as PersonLook | undefined) : undefined
  return {
    device,
    person: own ? personLayerOf(own, slug) : undefined,
    workspace: layerOfCarrier(current ? current.spec : settings.look),
  }
}

/** What the layers come to, with where each half came from. */
export const useResolvedLook = (): ResolvedLook => {
  const layers = useLayers()
  return resolveLayers(layers)
}

/** What the appearance comes to right now; re-checked each minute so auto turns at 07:00 and 19:00. */
export const useResolvedAppearance = (): Resolved => {
  const now = useNow(60_000)
  return resolveAppearance(appearanceOf(useResolvedLook()), now)
}

/** The palette and accent in force (what pages tint by): a palette's own, else Horizon's accent. */
export const useLook = (): Look => {
  const r = useResolvedLook()
  if (r.theme.kind === 'palette')
    return { theme: r.theme.theme, palette: paletteFor(r.theme.theme, r.theme.palette), colour: r.colour }
  return { theme: undefined, palette: r.palette, colour: r.colour }
}

/** Every theme profile this device syncs: mine and, in my suites, everyone's shared ones (the server narrows). Newest first. */
export const useThemeProfiles = (): ThemeProfileResource[] =>
  useLive(
    'ThemeProfile:all',
    async () =>
      (await byKind<ThemeProfileResource>('ThemeProfile', ALL)).sort((a, b) =>
        b.updated_at.localeCompare(a.updated_at),
      ),
    [],
  ) ?? []

/** The theme this person wears from the studio (`person_look.studio`), if any. */
export const useAppliedStudio = (): StudioLook | undefined => {
  const own = useSettings().person_look as PersonLook | undefined
  return studioOf(own)
}

export const useEdgesFrom = (id: string | undefined): Edge[] =>
  useLive(
    `EdgesFrom:${id ?? ''}`,
    async () =>
      id ? (await db.edges.where('from_id').equals(id).toArray()).filter((e) => !e.deleted_at) : [],
    [id],
  ) ?? []

export const useEdgesTo = (id: string | undefined): Edge[] =>
  useLive(
    `EdgesTo:${id ?? ''}`,
    async () => (id ? (await db.edges.where('to_id').equals(id).toArray()).filter((e) => !e.deleted_at) : []),
    [id],
  ) ?? []

export const useAllEdges = (): Edge[] =>
  useLive('Edges', async () => (await db.edges.toArray()).filter((e) => !e.deleted_at), []) ?? []

/** Map task id → project id from live belongs_to edges. */
export const useProjectOf = (): Map<string, string> => {
  const edges = useAllEdges()
  // one map per change of the edges, so what is derived from it (the graph's
  // layout) is not redone on every render
  return useMemo(() => {
    const m = new Map<string, string>()
    for (const e of edges) if (e.relation === 'belongs_to') m.set(e.from_id, e.to_id)
    return m
  }, [edges])
}

/** Learn's records in the current scope. */
export const useCourses = (): CourseResource[] | undefined => {
  const ws = useScope((s) => s.ws)
  return useLive(`Course:${ws}`, () => byKind<CourseResource>('Course', ws), [ws])
}

export const useModules = (): ModuleResource[] | undefined => {
  const ws = useScope((s) => s.ws)
  return useLive(`Module:${ws}`, () => byKind<ModuleResource>('Module', ws), [ws])
}

export const useLessons = (): LessonResource[] | undefined => {
  const ws = useScope((s) => s.ws)
  return useLive(`Lesson:${ws}`, () => byKind<LessonResource>('Lesson', ws), [ws])
}

export const useConcepts = (): ConceptResource[] | undefined => {
  const ws = useScope((s) => s.ws)
  return useLive(`Concept:${ws}`, () => byKind<ConceptResource>('Concept', ws), [ws])
}

export const useExercises = (): ExerciseResource[] | undefined => {
  const ws = useScope((s) => s.ws)
  return useLive(`Exercise:${ws}`, () => byKind<ExerciseResource>('Exercise', ws), [ws])
}

export const useSessions = (): SessionResource[] | undefined => {
  const ws = useScope((s) => s.ws)
  return useLive(`Session:${ws}`, () => byKind<SessionResource>('Session', ws), [ws])
}

/** Every live concept in every suite (Search). */
export const useAllConcepts = (): ConceptResource[] | undefined =>
  useLive(`Concept:${ALL}`, () => byKind<ConceptResource>('Concept', ALL), [])

export const usePages = (): PageResource[] | undefined => {
  const ws = useScope((s) => s.ws)
  return useLive(`Page:${ws}`, () => byKind<PageResource>('Page', ws), [ws])
}

export const useAllPages = (): PageResource[] | undefined =>
  useLive(`Page:${ALL}`, () => byKind<PageResource>('Page', ALL), [])

/**
 * The Page internal/docsync keeps for a file under docs/ (labelled doc=<its
 * path there>, e.g. `security-programme.md`), in any workspace: undefined
 * until the first read, null when there is no copy yet (it comes with a release).
 */
export const useDocPage = (path: string): PageResource | null | undefined =>
  useLive(
    `DocPage:${path}`,
    async () => (await byKind<PageResource>('Page', ALL)).find((p) => p.labels.doc === path) ?? null,
    [path],
  )

/** Pages attached to a project, task or page via belongs_to. */
export const usePagesFor = (parentId: string | undefined): PageResource[] => {
  const edges = useEdgesTo(parentId)
  const pages = useAllPages() ?? []
  const ids = new Set(edges.filter((e) => e.relation === 'belongs_to').map((e) => e.from_id))
  return pages.filter((n) => ids.has(n.id) && !n.deleted_at)
}

export const useLists = (): ListResource[] | undefined => {
  const ws = useScope((s) => s.ws)
  return useLive(`List:${ws}`, () => byKind<ListResource>('List', ws), [ws])
}

export const useAllLists = (): ListResource[] | undefined =>
  useLive(`List:${ALL}`, () => byKind<ListResource>('List', ALL), [])

/** Lists attached to a project, task or page via belongs_to. */
export const useListsFor = (parentId: string | undefined): ListResource[] => {
  const edges = useEdgesTo(parentId)
  const lists = useAllLists() ?? []
  const ids = new Set(edges.filter((e) => e.relation === 'belongs_to').map((e) => e.from_id))
  return lists.filter((l) => ids.has(l.id) && !l.deleted_at)
}

/** Every grant (they are not workspace-scoped), newest first. */
export const useGrants = (): GrantResource[] =>
  useLive(
    'Grants',
    async () =>
      (await byKind<GrantResource>('Grant', ALL))
        .filter((g) => !g.deleted_at)
        .sort((a, b) => (a.created_at < b.created_at ? 1 : -1)),
    [],
  ) ?? []

// Settings are kept on the device as well, so even the app's very first render
// (a cold start) has the dock, the appearance, the home screens as they were.
const SETTINGS_CACHE = 'settings-cache'
try {
  const cached = localStorage.getItem(SETTINGS_CACHE)
  if (cached) lastSeen.set('Settings', settingsDefaults(JSON.parse(cached) as Partial<SettingsSpec>))
} catch {
  /* private mode or a bad value: the defaults it is */
}

export const useSettings = (): SettingsSpec => {
  const base =
    useLive(
      'Settings',
      async () => {
        const r = await db.resources.get(settingsIdNow())
        const spec = (r?.spec as Partial<SettingsSpec>) ?? {}
        try {
          localStorage.setItem(SETTINGS_CACHE, JSON.stringify(spec))
        } catch {
          /* no room: the next cold start shows defaults for a frame */
        }
        return settingsDefaults(spec)
      },
      [],
    ) ?? settingsDefaults()
  const current = useCurrentWorkspace()
  return current?.spec.today_cap ? { ...base, today_cap: current.spec.today_cap } : base
}

export const useRejectedOps = (): OpRecord[] =>
  useLiveQuery(
    async () => await db.ops.where('state').equals('rejected').reverse().sortBy('created_at'),
    [],
  ) ?? []

export const useRecentOps = (limit = 200): OpRecord[] =>
  useLiveQuery(
    async () =>
      (await db.ops.orderBy('created_at').reverse().limit(limit).toArray()).filter(
        (o) => o.state !== 'rejected',
      ),
    [limit],
  ) ?? []

export { META }

/** Every Day record (not workspace-scoped), newest date first. */
export const useDays = (): DayResource[] =>
  useLive(
    'Days',
    async () =>
      (await byKind<DayResource>('Day', ALL))
        .filter((d) => !d.deleted_at)
        .sort((a, b) => (a.spec.date < b.spec.date ? 1 : -1)),
    [],
  ) ?? []

/** Every Date (not workspace-scoped), live ones only; lib/dates.ts orders them. */
export const useDates = (): DateResource[] =>
  useLive('Dates', async () => (await byKind<DateResource>('Date', ALL)).filter((d) => !d.deleted_at), []) ??
  []

/** Every Person (not workspace-scoped), live ones only, by name. */
export const usePeople = (): PersonResource[] =>
  useLive(
    'People',
    async () =>
      (await byKind<PersonResource>('Person', ALL))
        .filter((p) => !p.deleted_at)
        .sort((a, b) => a.spec.name.localeCompare(b.spec.name)),
    [],
  ) ?? []

/**
 * Who each thing points at: task or date id → the people it references. One
 * indexed query for every row that shows a person chip.
 */
export const usePersonLinks = (): Map<string, PersonResource[]> =>
  useLive(
    'PersonLinks',
    async () => {
      const people = (
        (await db.resources.where('kind').equals('Person').toArray()) as PersonResource[]
      ).filter((p) => !p.deleted_at)
      const byId = new Map(people.map((p) => [p.id, p]))
      const out = new Map<string, PersonResource[]>()
      if (!people.length) return out
      for (const e of await db.edges
        .where('to_id')
        .anyOf([...byId.keys()])
        .toArray()) {
        if (e.deleted_at || e.relation !== 'references') continue
        out.set(e.from_id, [...(out.get(e.from_id) ?? []), byId.get(e.to_id)!])
      }
      return out
    },
    [],
  ) ?? EMPTY_LINKS

const EMPTY_LINKS = new Map<string, PersonResource[]>()

/**
 * Names and kinds by id for everything a proposal can name (tasks, projects,
 * pages, lists, people, dates), so a card can say "Link “Card” to “Sam”".
 */
export const useNames = (): {
  titleOf: (id: string) => string | undefined
  kindOf: (id: string) => string | undefined
} => {
  const names =
    useLive(
      'Names',
      async () => {
        const out = new Map<string, [string, string]>()
        for (const r of await db.resources
          .where('kind')
          .anyOf(['Task', 'Project', 'Page', 'List', 'Person', 'Date'])
          .toArray()) {
          if (r.deleted_at) continue
          const sp = r.spec as { title?: string; name?: string; me?: boolean }
          out.set(r.id, [sp.me ? 'You' : (sp.title ?? sp.name ?? r.name ?? ''), r.kind])
        }
        return out
      },
      [],
    ) ?? EMPTY_NAMES
  return { titleOf: (id) => names.get(id)?.[0], kindOf: (id) => names.get(id)?.[1] }
}
const EMPTY_NAMES = new Map<string, [string, string]>()

// ---- Files (docs/areas/files.md) ------------------------------------------------

/** Every File record of the current suite, deleted ones too (the Files app's Trash reads them). */
export const useFilesWithTrash = (): FileResource[] | undefined => {
  const ws = useScope((s) => s.ws)
  return useLive(
    `File+trash:${ws}`,
    async () =>
      (ws === ALL
        ? await db.resources.where('kind').equals('File').toArray()
        : await db.resources.where('[labels.ws+kind]').equals([ws, 'File']).toArray()) as FileResource[],
    [ws],
  )
}
